import { ROUTES, hash, slugify } from "./routes";

const app = (label, tile, fragment) => ({
  label,
  tile,
  fragment: fragment ?? slugify(label),
  href: hash(ROUTES.sourceToPay, fragment ?? slugify(label)),
});

const entry = (route) => (label, blurb, fragment) => ({
  label,
  blurb,
  fragment: fragment ?? slugify(label),
  href: hash(route, fragment ?? slugify(label)),
});

const s2pEntry = entry(ROUTES.sourceToPay);
const solEntry = entry(ROUTES.solutions);
const platEntry = entry(ROUTES.platform);

/** The 7 headline Source-to-Pay apps promoted in nav + footer. */
export const FEATURED_S2P = [
  s2pEntry("Vendor Onboarding", "Self-serve registration"),
  s2pEntry("Purchase Requisition", "Guided intake and approvals"),
  s2pEntry("Purchase Order", "Automated PO issuance"),
  s2pEntry("E-Auctions", "Live competitive bidding"),
  s2pEntry("Request for Quotation", "Structured sourcing events"),
  s2pEntry("Goods Receipt Note", "Receipt and quality capture"),
  s2pEntry("Invoice", "Touchless three-way match"),
];

export const SOLUTION_LINKS = [
  solEntry("Digital Transformation", "Retire paper and email trails"),
  solEntry("Workflow Automation", "Rules, routing and SLAs"),
  solEntry("Case Management", "Structured issue resolution"),
  solEntry("Marketplace", "Curated catalogue buying"),
  solEntry("Procurement Automation", "Requisition to payment"),
  solEntry("Vendor Management", "Lifecycle in one system"),
  solEntry("Supply Chain Management", "Visibility across tiers"),
];

export const PLATFORM_LINKS = [
  platEntry("Low-Code App Builder", "Ship internal apps in days"),
  platEntry("Form Builder", "Drag-and-drop data capture"),
  platEntry("Workflow Builder", "Visual process design"),
  platEntry("Dashboard & Reports", "Operational reporting", "dashboard-reports"),
  platEntry("Analytics", "Self-serve exploration"),
  platEntry("AI Engine", "Agents, extraction, insight"),
  platEntry("API & Integrations", "ERP and best-of-breed", "api-integrations"),
  platEntry("Role Management", "Granular permissions"),
];

/** Mega-menu definitions keyed by nav tab id. */
export const MENUS = Object.freeze({
  s2p: {
    kicker: "Source to Pay",
    title: "Source-to-Pay Suite",
    blurb:
      "One connected flow from sourcing event to settled invoice — every module AI-assisted, audit-ready and live on day one.",
    href: ROUTES.sourceToPay,
    icon: "suite",
    groups: [
      {
        heading: "Vendor Management",
        sub: "Transform vendor management with AI-powered apps.",
        items: [
          app("Vendor Onboarding", "#0F1D29"),
          app("Service Entry Sheet", "#3E5A55"),
          app("Purchase Requisition", "#B4653A"),
          app("Advance Shipping Notice", "#9E4A3C"),
          app("Purchase Order", "#8A5B7A"),
          app("Goods Receipt Note", "#A07C3F"),
          app("E-Auctions", "#1F3A4D"),
          app("Invoice", "#2F4F6B"),
          app("Request for Quotation", "#B4653A"),
        ],
      },
      {
        heading: "Other Source to Pay apps",
        sub: "Apps for contracts, compliance, performance, risk and supplier communication.",
        items: [
          app("Contract Management", "#9E4A3C"),
          app("Supplier Certification", "#8A5B7A"),
          app("Supply Chain Issue Management", "#0F1D29"),
          app("Supplier Rating", "#2F4F6B"),
          app("Supplier Compliance Management", "#B4653A"),
          app("Supplier Performance Management", "#1F3A4D"),
          app("Supplier Bulletin Management", "#3E5A55"),
        ],
      },
    ],
    items: FEATURED_S2P,
  },
  solutions: {
    kicker: "Solutions",
    title: "Solutions",
    blurb:
      "Outcome-led programmes for procurement, finance and operations teams modernising how work gets done.",
    href: ROUTES.solutions,
    icon: "transform",
    items: SOLUTION_LINKS,
  },
  platform: {
    kicker: "Platform",
    title: "The Mercoris Platform",
    blurb:
      "A low-code foundation your teams extend themselves — builders, data, AI and enterprise-grade controls.",
    href: ROUTES.platform,
    icon: "builder",
    items: PLATFORM_LINKS,
  },
});

/** Flat top-level nav order. `menu` links a tab to a MENUS key. */
export const PRIMARY_NAV = [
  { id: "home", label: "Home", to: ROUTES.home },
  { id: "s2p", label: "Source to Pay", to: ROUTES.sourceToPay, menu: "s2p" },
  { id: "solutions", label: "Solutions", to: ROUTES.solutions, menu: "solutions" },
  { id: "platform", label: "Platform", to: ROUTES.platform, menu: "platform" },
  { id: "about", label: "About Us", to: ROUTES.about },
  { id: "contact", label: "Contact Us", to: ROUTES.contact },
];

/** Footer columns mirror the navbar structure exactly. `titleKey`/`navId`/
 *  `fragment` are stable, English-derived lookup keys for translated
 *  display text — never re-derived from translated copy. */
export const FOOTER_COLUMNS = [
  {
    titleKey: "sitemap",
    links: [
      { navId: "home", href: ROUTES.home },
      { navId: "s2p", href: ROUTES.sourceToPay },
      { navId: "solutions", href: ROUTES.solutions },
      { navId: "platform", href: ROUTES.platform },
      { navId: "about", href: ROUTES.about },
      { navId: "contact", href: ROUTES.contact },
    ],
  },
  {
    titleKey: "sourceToPay",
    links: [
      ...FEATURED_S2P.map(({ fragment, href }) => ({ fragment, href })),
      { viewAll: true, href: hash(ROUTES.sourceToPay, "modules") },
    ],
  },
  {
    titleKey: "solutions",
    links: SOLUTION_LINKS.map(({ fragment, href }) => ({ fragment, href })),
  },
  {
    titleKey: "platform",
    links: PLATFORM_LINKS.map(({ fragment, href }) => ({ fragment, href })),
  },
];
